import { getLocalTimeZone, parseDate } from "@internationalized/date";
import { useQuery } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { queryFnGET } from "api/tanStackQuery/helpers";
import { queryClient } from "api/tanStackQuery/queryClient";
import { withErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import { format, subDays } from "date-fns";
import { DayPlanTrackingModal } from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/DayPlanTrackingModal";
import { TableContent } from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/TableContent/TableContent";
import { filterDayPlanTrackings } from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/TableContent/utils";
import { TableFilter } from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/TableFilter/TableFilter";
import {
  FilterType,
  initStates,
} from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/TableFilter/constants";
import {
  DayPlanTrackingResponseType,
  DayPlanTrackingType,
  DAYPLAN_TABLE_COLUMNS,
} from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Skeleton, Stack, Grid, Text, Box } from "shared/components/Spor";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import { useTheme } from "styled-components";
import { useWebsocket } from "websocket/useWebsocket";
import {
  BackendIdentifier,
  DayPlanTrackingMessage,
  WebsocketTopic,
} from "websocket/websocketSharedWorkerTypes";

const getDayplanTrackingFilterSettings = (): FilterType | null => {
  const parsed = JSON.parse(
    sessionStorage.getItem("dayplanTrackingFilterSettings") ?? "null",
  );
  return parsed
    ? { ...parsed, chosenDate: parseDate(parsed?.chosenDate) }
    : null;
};

const setSessionStorageDayplanTrackingFilterSettings = (
  dayplanTrackingFilterSettings: FilterType,
) => {
  const date = format(
    dayplanTrackingFilterSettings.chosenDate.toDate(getLocalTimeZone()),
    "yyyy-MM-dd",
  );

  sessionStorage.setItem(
    "dayplanTrackingFilterSettings",
    JSON.stringify({
      ...dayplanTrackingFilterSettings,
      chosenDate: date,
    }),
  );
};

const DayPlanTrackings = () => {
  const theme = useTheme();
  const [filterParams, setFilterParams] = useState<FilterType>(
    getDayplanTrackingFilterSettings() ?? initStates,
  );
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedDayPlanTracking, setSelectedDayPlanTracking] =
    useState<DayPlanTrackingType | null>();

  useEffect(() => {
    setSessionStorageDayplanTrackingFilterSettings(filterParams);
  }, [filterParams]);

  const selectedDate = format(
    filterParams.chosenDate.toDate(getLocalTimeZone()),
    "yyyy-MM-dd",
  );

  const queryKey = useMemo(
    () => ["dayPlanTrackings", selectedDate],
    [selectedDate],
  );

  const messageHandler = useCallback(
    ({ message }: DayPlanTrackingMessage) => {
      queryClient.setQueryData<DayPlanTrackingType[]>(queryKey, (prev) =>
        prev
          ? [
              message,
              ...prev.filter(
                (d) =>
                  !(
                    d.employeeId === message.employeeId &&
                    d.dayPlanDate === message.dayPlanDate
                  ),
              ),
            ]
          : [message],
      );
    },
    [queryKey],
  );

  useWebsocket(BackendIdentifier.DROPS_BACKEND, {
    [WebsocketTopic.DAY_PLAN_TRACKING]: messageHandler,
  });

  // One day earlier to capture shifts that span past midnight
  const selectedPrevDate = format(
    subDays(filterParams.chosenDate.toDate(getLocalTimeZone()), 1),
    "yyyy-MM-dd",
  );

  const { isPending, isSuccess, isError, data } = useQuery({
    queryKey,
    refetchOnWindowFocus: true,
    queryFn: ({ signal }) =>
      queryFnGET<DayPlanTrackingResponseType>({
        signal,
        url: `${getBackendUrl()}/day-plan/trackings?fromDate=${selectedPrevDate}&toDate=${selectedDate}`,
      }).then(
        (dayPlanTrackingResponse) => dayPlanTrackingResponse.dayPlanTrackings,
      ),
  });

  const filteredDayPlanTrackings = useMemo(
    () => filterDayPlanTrackings(data ?? [], filterParams),
    [data, filterParams],
  );

  if (isError) {
    return <FailureMessage />;
  }

  return (
    <Stack
      backgroundColor="semantics.colorBackgroundMain"
      padding="2"
      borderRadius="xs"
      borderColor={theme.colorOutlineSecondary}
      borderWidth="1px"
      overflowX="scroll"
      height="max-content"
    >
      <Stack gap="2" minWidth="1124px">
        <TableFilter
          setFilterParams={setFilterParams}
          filterParams={filterParams}
        />
        <Box>
          <Grid
            gridTemplateColumns="160px repeat(3, 100px) 2fr 100px repeat(2, 140px) 114px;"
            borderRadius="xs"
            overflow="hidden"
          >
            <Grid
              gridRow={2}
              gridColumn="1 / -1"
              gridTemplateColumns="subgrid"
              overflow="scroll"
              maxHeight="25vh"
            >
              {isPending && (
                <>
                  <Skeleton height="6" gridColumn="1 / -1" />
                  <Skeleton height="6" gridColumn="1 / -1" />
                  <Skeleton height="6" gridColumn="1 / -1" />
                  <Skeleton height="6" gridColumn="1 / -1" />
                </>
              )}

              {isSuccess && (
                <TableContent
                  filteredDayPlanTrackings={filteredDayPlanTrackings}
                  onRowClick={(dayPlanTracking: DayPlanTrackingType) => {
                    setSelectedDayPlanTracking(dayPlanTracking);
                    setModalOpen(true);
                  }}
                />
              )}
            </Grid>

            {/* HEADER ROW */}
            <Grid
              gridRow={1}
              gridColumn="1 / -1"
              gridTemplateColumns="subgrid"
              backgroundColor="semantics.colorBackgroundSecondary"
              borderTopRadius="xs"
              border={`1px solid ${theme.colorOutlineSecondary}`}
            >
              {DAYPLAN_TABLE_COLUMNS.map((columnTitle) => (
                <Text
                  key={columnTitle}
                  variant="sm"
                  px="3"
                  py="1.5"
                  fontWeight="bold"
                >
                  {columnTitle}
                </Text>
              ))}
            </Grid>
          </Grid>

          {isSuccess && selectedDayPlanTracking && (
            <DayPlanTrackingModal
              showModal={modalOpen}
              closeModal={() => setModalOpen(false)}
              dayPlanTracking={selectedDayPlanTracking}
            />
          )}
        </Box>
      </Stack>
    </Stack>
  );
};

export default withErrorBoundary(DayPlanTrackings);
